import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Truck, Ticket, Wallet, QrCode, Landmark, PackageOpen, Gift } from 'lucide-react';
import { fmtRM2, DISTRICTS, MALAYSIA_STATES, computeDeliveryFee } from '../data/mockData';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { EmptyState } from '../components/commerce/EmptyState';
import api, { formatApiError } from '../lib/api';
import { toast } from 'sonner';

const PAY_METHODS = [
  { id: 'CASH', label: 'Cash', icon: Wallet, desc: 'Pay cash to KELATE delivery staff.' },
  { id: 'TNG_QR', label: 'TNG QR', icon: QrCode, desc: 'Scan the payment QR shown by KELATE delivery staff.' },
  { id: 'BANK_TRANSFER', label: 'Bank Transfer', icon: Landmark, desc: 'Use the payment details provided by KELATE delivery staff.' },
];

export default function CheckoutPage() {
  const { cart, deliverySettings, placeOrder, customProducts, merchantById, wallets, vouchers } = useApp();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [address, setAddress] = useState({ name: user?.name || '', phone: user?.phone || '', line1: '', district: 'Kota Bharu', state: 'Kelantan' });
  const [method, setMethod] = useState('CASH');
  const [voucherCode, setVoucherCode] = useState(localStorage.getItem('kelate_voucher') || '');
  const [refCode, setRefCode] = useState('');
  const [refApplied, setRefApplied] = useState(null);
  const [placing, setPlacing] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    if (user) setAddress((a) => ({ ...a, name: a.name || user.name || '', phone: a.phone || user.phone || '' }));
  }, [user]);

  const selected = useMemo(() => cart.filter((c) => c.selected).map((c) => ({ ...c, product: customProducts.find((p) => p.id === c.productId) })).filter((c) => c.product), [cart, customProducts]);

  const groups = useMemo(() => {
    const g = {};
    selected.forEach((c) => { (g[c.product.merchantId] = g[c.product.merchantId] || []).push(c); });
    return g;
  }, [selected]);

  const subtotal = selected.reduce((sum, c) => sum + c.product.price * c.qty, 0);
  const voucher = vouchers.find((v) => v.active && v.code === voucherCode);
  const { fee, zone } = computeDeliveryFee(address, deliverySettings);
  const merchantIds = Object.keys(groups);
  const deliveryTotal = fee * merchantIds.length;
  let discount = 0;
  if (voucher) {
    if (voucher.scope === 'PLATFORM') {
      const firstSub = (groups[merchantIds[0]] || []).reduce((s, c) => s + c.product.price * c.qty, 0);
      if (firstSub >= (voucher.minSpend || 0)) discount = voucher.type === 'PERCENT' ? Math.round(firstSub * voucher.value) / 100 : Math.min(voucher.value, firstSub);
    } else {
      const groupSub = (groups[voucher.merchantId] || []).reduce((s, c) => s + c.product.price * c.qty, 0);
      if (groupSub >= (voucher.minSpend || 0)) discount = voucher.type === 'PERCENT' ? Math.round(groupSub * voucher.value) / 100 : Math.min(voucher.value, groupSub);
    }
  }
  const referralDiscount = refApplied?.discount || 0;
  const total = Math.max(0, subtotal - discount - referralDiscount) + deliveryTotal;
  const walletBalance = wallets[user?.email] || 0;

  const applyReferral = async () => {
    const code = refCode.trim().toUpperCase();
    if (!code) return;
    try {
      const res = await api.get(`/referrals/validate/${code}`);
      if (!res.data.ok) return toast.error(res.data.reason === 'self' ? "You can't use your own referral code" : 'Invalid referral code', res.data.reason === 'self' ? undefined : { description: 'Check the code with your friend and try again.' });
      const prog = res.data.program;
      if (!prog.enabled) return toast.error('Referral program is not active right now');
      if (subtotal < (prog.minSpend || 0)) return toast.error(`Referral needs a minimum spend of ${fmtRM2(prog.minSpend)}`);
      const disc = prog.discountType === 'PERCENT' ? Math.round(subtotal * prog.discountValue) / 100 : Math.min(prog.discountValue, subtotal);
      setRefApplied({ code, discount: disc, ownerEmail: res.data.ownerEmail, ownerName: res.data.ownerName });
      toast.success('Referral code applied', { description: `${fmtRM2(disc)} off, thanks to ${res.data.ownerName}.` });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!user) {
    navigate(`/account/login?redirect=/checkout`);
    return null;
  }
  if (selected.length === 0) {
    return (
      <div className="mx-auto max-w-[1280px] px-4 py-10">
        <EmptyState icon={PackageOpen} title="Nothing selected for checkout" description="Go back to your cart and select items." actionLabel="Open Cart" actionTo="/cart" testId="checkout-empty" />
      </div>
    );
  }

  const submit = () => {
    if (!address.name || !address.phone || !address.line1) {
      setError('Please complete your delivery address.');
      return;
    }
    if (method === 'WALLET' && walletBalance < total) {
      setError(`Insufficient wallet balance (${fmtRM2(walletBalance)}). Top up in My KELATE → Wallet.`);
      return;
    }
    const orders = placeOrder({
      address, paymentMethod: method,
      voucher: discount > 0 && voucher ? voucher : null,
      referral: refApplied ? { ...refApplied, usedByEmail: user.email, usedByName: user.name } : null,
      buyerKey: user.email,
    });
    if (orders?.error === 'insufficient-wallet') {
      setError(`Insufficient wallet balance (${fmtRM2(orders.balance)}). Top up in My KELATE → Wallet.`);
      return;
    }
    localStorage.removeItem('kelate_voucher');
    navigate('/checkout/success', { state: { orderNumbers: orders.map((o) => o.orderNumber), orderIds: orders.map((o) => o.id), total, method } });
  };

  return (
    <div data-testid="checkout-page" className="mx-auto max-w-[1280px] px-4 py-8">
      <h1 className="font-display text-2xl font-semibold text-kelate-darkwood sm:text-3xl">Checkout</h1>
      <p className="mt-1 text-sm text-kelate-soft">No online payment needed — pay when your KELATE delivery arrives.</p>

      <div className="mt-6 grid gap-6 lg:grid-cols-[7fr_3fr]">
        <div className="space-y-5">
          <section className="rounded-3xl bg-kelate-surface shadow-[0_1px_4px_rgba(81,68,55,0.08)] p-5" data-testid="checkout-address">
            <h2 className="flex items-center gap-2 text-sm font-semibold text-kelate-ink"><MapPin size={15} className="text-kelate-wood" /> Delivery Address</h2>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <input data-testid="address-name" placeholder="Full name" value={address.name} onChange={(e) => setAddress({ ...address, name: e.target.value })} className="h-10 rounded-sm border border-kelate-line bg-kelate-bg px-3 text-sm outline-none focus:border-kelate-wood" />
              <input data-testid="address-phone" placeholder="Phone number" value={address.phone} onChange={(e) => setAddress({ ...address, phone: e.target.value })} className="h-10 rounded-sm border border-kelate-line bg-kelate-bg px-3 text-sm outline-none focus:border-kelate-wood" />
              <input data-testid="address-line1" placeholder="Address (street, lot, kampung)" value={address.line1} onChange={(e) => setAddress({ ...address, line1: e.target.value })} className="h-10 rounded-sm border border-kelate-line bg-kelate-bg px-3 text-sm outline-none focus:border-kelate-wood sm:col-span-2" />
              <select data-testid="address-district" value={address.district} onChange={(e) => setAddress({ ...address, district: e.target.value })} className="h-10 rounded-sm border border-kelate-line bg-kelate-bg px-2 text-sm outline-none focus:border-kelate-wood">
                {DISTRICTS.map((d) => <option key={d}>{d}</option>)}
              </select>
              <select data-testid="address-state" value={address.state} onChange={(e) => setAddress({ ...address, state: e.target.value })} className="h-10 rounded-sm border border-kelate-line bg-kelate-bg px-2 text-sm outline-none focus:border-kelate-wood">
                {MALAYSIA_STATES.map((s) => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div className="mt-3 flex items-center justify-between rounded-sm bg-secondary/60 px-3 py-2 text-xs" data-testid="delivery-zone">
              <span className="flex items-center gap-1.5 text-kelate-ink"><Truck size={13} className="text-kelate-sage" /> {zone}</span>
              <span className="font-semibold text-kelate-darkwood">{fmtRM2(fee)} / merchant</span>
            </div>
          </section>

          <section className="rounded-3xl bg-kelate-surface shadow-[0_1px_4px_rgba(81,68,55,0.08)] p-5" data-testid="checkout-items">
            <h2 className="text-sm font-semibold text-kelate-ink">Order Items</h2>
            {merchantIds.map((mid) => {
              const merchant = merchantById(mid);
              return (
                <div key={mid} className="mt-4 rounded-sm border border-kelate-line">
                  <div className="border-b border-kelate-line bg-secondary/40 px-3 py-2 text-xs font-semibold text-kelate-ink">{merchant?.name || 'Store'} <span className="font-normal text-kelate-soft">· KELATE Delivery {fmtRM2(fee)}</span></div>
                  {groups[mid].map((c) => (
                    <div key={`${c.productId}|${c.variant}`} className="flex items-center gap-3 px-3 py-2.5">
                      <img src={c.product.image} alt="" className="h-12 w-12 rounded-sm border border-kelate-line object-cover" />
                      <div className="flex-1 text-sm">
                        <div className="font-medium text-kelate-ink">{c.product.name}</div>
                        {c.variant && <div className="text-xs text-kelate-soft">{c.variant}</div>}
                      </div>
                      <div className="text-sm text-kelate-soft">×{c.qty}</div>
                      <div className="w-20 text-right text-sm font-semibold text-kelate-darkwood">{fmtRM2(c.product.price * c.qty)}</div>
                    </div>
                  ))}
                </div>
              );
            })}
            <div className="mt-4 flex gap-2">
              <input data-testid="checkout-voucher-input" value={voucherCode} onChange={(e) => setVoucherCode(e.target.value.toUpperCase())} placeholder="Voucher code" className="h-9 flex-1 rounded-sm border border-kelate-line bg-kelate-bg px-2.5 text-sm outline-none focus:border-kelate-wood" />
              <span className="flex items-center gap-1 text-xs text-kelate-soft"><Ticket size={12} className="text-kelate-wood" /> {voucher && discount > 0 ? `${voucher.code} applied` : 'Enter code'}</span>
            </div>
            <div className="mt-2 flex gap-2">
              <input data-testid="checkout-referral-input" value={refCode} onChange={(e) => { setRefCode(e.target.value.toUpperCase()); setRefApplied(null); }} placeholder="Friend's referral code (e.g. KEL-ABC123)" className="h-9 flex-1 rounded-sm border border-kelate-line bg-kelate-bg px-2.5 text-sm outline-none focus:border-kelate-wood" />
              {refApplied ? (
                <span className="flex items-center gap-1 text-xs font-medium text-kelate-sage" data-testid="referral-applied"><Gift size={12} /> {refApplied.code} applied</span>
              ) : (
                <button data-testid="referral-apply-btn" onClick={applyReferral} className="flex items-center gap-1 rounded-sm border border-kelate-darkwood px-3 text-xs font-medium text-kelate-darkwood hover:bg-secondary"><Gift size={12} /> Apply</button>
              )}
            </div>
          </section>

          <section className="rounded-3xl bg-kelate-surface shadow-[0_1px_4px_rgba(81,68,55,0.08)] p-5" data-testid="checkout-payment">
            <h2 className="text-sm font-semibold text-kelate-ink">Payment Method</h2>
            <p className="mt-1 text-xs text-kelate-soft">Choose how you want to pay when your order arrives.</p>
            <div className="mt-4 space-y-2">
              {PAY_METHODS.map((m) => (
                <label key={m.id} data-testid={`pay-${m.id.toLowerCase()}`} className={`flex cursor-pointer items-start gap-3 rounded-sm border p-3 transition-colors ${method === m.id ? 'border-kelate-darkwood bg-secondary/50' : 'border-kelate-line hover:border-kelate-wood'}`}>
                  <input type="radio" name="payment" checked={method === m.id} onChange={() => setMethod(m.id)} className="mt-1 accent-kelate-darkwood" />
                  <m.icon size={17} className="mt-0.5 text-kelate-wood" />
                  <span>
                    <span className="block text-sm font-medium text-kelate-ink">{m.label} <span className="ml-1 rounded-sm bg-secondary px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-kelate-darkwood">Pay on Delivery</span></span>
                    <span className="mt-0.5 block text-xs text-kelate-soft">{m.desc}</span>
                  </span>
                </label>
              ))}
              <label data-testid="pay-wallet" className={`flex cursor-pointer items-start gap-3 rounded-sm border p-3 transition-colors ${method === 'WALLET' ? 'border-kelate-darkwood bg-secondary/50' : 'border-kelate-line hover:border-kelate-wood'}`}>
                <input type="radio" name="payment" checked={method === 'WALLET'} onChange={() => setMethod('WALLET')} className="mt-1 accent-kelate-darkwood" />
                <Wallet size={17} className="mt-0.5 text-kelate-wood" />
                <span>
                  <span className="block text-sm font-medium text-kelate-ink">KELATE Wallet <span className="ml-1 rounded-sm bg-[#DCE5D8] px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#3E5232]">Pay Now</span></span>
                  <span className="mt-0.5 block text-xs text-kelate-soft">Balance: <b className="text-kelate-darkwood">{fmtRM2(walletBalance)}</b> · instant payment, nothing to pay on delivery.{walletBalance < total && ' Insufficient — top up in My KELATE → Wallet.'}</span>
                </span>
              </label>
            </div>
          </section>
        </div>

        <aside className="h-fit rounded-3xl bg-kelate-surface shadow-[0_1px_4px_rgba(81,68,55,0.08)] p-5 lg:sticky lg:top-24" data-testid="checkout-summary">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-kelate-soft">Order Summary</h2>
          <div className="mt-4 space-y-2 text-sm">
            <div className="flex justify-between text-kelate-soft"><span>Items</span><span data-testid="summary-items">{fmtRM2(subtotal)}</span></div>
            <div className="flex justify-between text-kelate-soft"><span>Delivery ({merchantIds.length} merchant{merchantIds.length > 1 ? 's' : ''})</span><span data-testid="summary-delivery">{fmtRM2(deliveryTotal)}</span></div>
            {discount > 0 && <div className="flex justify-between text-kelate-sage"><span>Voucher ({voucher.code})</span><span data-testid="summary-voucher">-{fmtRM2(discount)}</span></div>}
            {referralDiscount > 0 && <div className="flex justify-between text-kelate-sage"><span>Referral ({refApplied.code})</span><span data-testid="summary-referral">-{fmtRM2(referralDiscount)}</span></div>}
            <div className="flex justify-between border-t border-kelate-line pt-2 text-lg font-semibold text-kelate-darkwood"><span>Total</span><span data-testid="summary-total">{fmtRM2(total)}</span></div>
          </div>
          <div className="mt-3 rounded-sm bg-secondary/60 px-3 py-2 text-xs text-kelate-ink">
            Payment: {method === 'WALLET' ? <b>PAID NOW · KELATE WALLET</b> : <><b>PAY ON DELIVERY</b> · {PAY_METHODS.find((m) => m.id === method)?.label}</>}
          </div>
          {error && <p className="mt-2 text-xs text-kelate-red" data-testid="checkout-error">{error}</p>}
          <button data-testid="place-order-btn" onClick={submit} disabled={placing} className="mt-4 w-full rounded-sm bg-kelate-darkwood py-3.5 text-sm font-semibold uppercase tracking-wide text-kelate-surface hover:bg-kelate-wood disabled:opacity-50">
            {placing ? 'Placing Order...' : 'Place Order'}
          </button>
        </aside>
      </div>
    </div>
  );
}
